import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { WhatsNewItem } from '../data/whats-new.data';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WhatsNewService {
  private readonly LAST_SEEN_VERSION_KEY = 'lastSeenVersion';
  private hasUnseenUpdates$ = new BehaviorSubject<boolean>(false);
  private apiUrl = `${environment.apiUrl}/app-changes`;
  private whatsNewCache: WhatsNewItem[] = [];

  constructor(private http: HttpClient) {
    this.loadWhatsNew();
  }

  /**
   * Load changelog from API
   */
  private async loadWhatsNew(): Promise<void> {
    try {
      const data = await firstValueFrom(
        this.http.get<WhatsNewItem[]>(`${this.apiUrl}/recent?limit=10`)
      );
      this.whatsNewCache = data;
      this.checkForUpdates();
    } catch (error) {
      console.error('Failed to load What\'s New data:', error);
      this.whatsNewCache = [];
    }
  }

  /**
   * Get all changelog entries (returns Observable for async loading)
   */
  getWhatsNew(): Observable<WhatsNewItem[]> {
    return this.http.get<WhatsNewItem[]>(`${this.apiUrl}/recent?limit=10`);
  }

  /**
   * Get all changelog entries (synchronous from cache)
   */
  getWhatsNewSync(): WhatsNewItem[] {
    return this.whatsNewCache;
  }

  /**
   * Get the latest changelog entry
   */
  getLatestUpdate(): WhatsNewItem | null {
    return this.whatsNewCache.length > 0 ? this.whatsNewCache[0] : null;
  }

  /**
   * Check if there are unseen updates
   */
  hasUnseenUpdates(): Observable<boolean> {
    return this.hasUnseenUpdates$.asObservable();
  }

  /**
   * Mark current version as seen
   */
  markAsSeen(version: string): void {
    localStorage.setItem(this.LAST_SEEN_VERSION_KEY, version);
    this.checkForUpdates();
  }

  /**
   * Get the last seen version
   */
  getLastSeenVersion(): string | null {
    return localStorage.getItem(this.LAST_SEEN_VERSION_KEY);
  }

  /**
   * Check if there are new updates since last visit
   */
  private checkForUpdates(): void {
    const lastSeenVersion = this.getLastSeenVersion();
    const latestUpdate = this.getLatestUpdate();
    
    if (!latestUpdate) {
      this.hasUnseenUpdates$.next(false);
      return;
    }

    if (!lastSeenVersion) {
      // First time visiting
      this.hasUnseenUpdates$.next(true);
      return;
    }

    // Compare versions
    const hasUnseen = this.compareVersions(latestUpdate.version, lastSeenVersion) > 0;
    this.hasUnseenUpdates$.next(hasUnseen);
  }

  /**
   * Compare two version strings
   * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
   */
  private compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const part1 = parts1[i] || 0;
      const part2 = parts2[i] || 0;

      if (part1 > part2) return 1;
      if (part1 < part2) return -1;
    }

    return 0;
  }
}

